/* Shared engine for the full-viewport, one-screen-per-scroll editorial pages.
   One instance per [data-editorial-root] on the page (there's normally exactly one:
   whichever of editorial-home / editorial-product / editorial-about rendered). */
(function () {
  // The announcement ticker is left visible above the full-screen sections
  // (see editorial-shell.css); its height varies by settings and can wrap to
  // two lines on narrow screens, so measure it for real instead of guessing.
  // Grouped sections render with ids like
  // "shopify-section-sections--{group-id}__annoucement" — the numeric group
  // id varies per theme/environment, so match on the stable suffix.
  function getTickerEl() {
    return document.querySelector('[id*="__annoucement"]');
  }

  function syncTickerHeight() {
    var el = getTickerEl();
    var h = el ? el.offsetHeight : 0;
    document.documentElement.style.setProperty('--ed-ticker-h', h + 'px');
  }

  function initTickerHeightSync() {
    syncTickerHeight();
    window.addEventListener('resize', syncTickerHeight);
    // Re-measure once webfonts finish loading/swapping — the ticker can grow
    // a few pixels taller after the swap, after our first measurement ran.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(syncTickerHeight);
    }
    var el = getTickerEl();
    if (el && window.ResizeObserver) {
      new ResizeObserver(syncTickerHeight).observe(el);
    }
  }

  function EditorialScroll(root) {
    this.root = root;
    this.scroller = root.querySelector('[data-editorial-scroll]');
    this.sections = this.scroller ? Array.prototype.slice.call(this.scroller.children).filter(function (n) {
      return n.tagName === 'SECTION';
    }) : [];
    this.dotsEl = root.querySelector('[data-editorial-dots]');
    this.numEl = root.querySelector('[data-editorial-counter-num]');
    this.labelEl = root.querySelector('[data-editorial-counter-label]');
    this.active = 0;
    this.lock = false;
    this.settleTimer = null;
    this.unlockTimer = null;
    this.lastWheelAt = 0;
    this.dots = [];
    this.build();
    this.bind();
    this.render();
  }

  EditorialScroll.prototype.build = function () {
    if (!this.dotsEl) return;
    var self = this;
    this.sections.forEach(function (section, i) {
      var label = section.getAttribute('data-screen-label') || '';
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.title = label;
      btn.addEventListener('click', function () { self.jump(i); });
      self.dotsEl.appendChild(btn);
      self.dots.push(btn);
    });
  };

  EditorialScroll.prototype.bind = function () {
    var self = this;
    if (this.scroller) {
      this.scroller.addEventListener('scroll', function () { self.onScroll(); }, { passive: true });
      this.scroller.addEventListener('wheel', function (e) { self.onWheel(e); }, { passive: false });
    }
    window.addEventListener('keydown', function (e) { self.onKey(e); });

    this.root.querySelectorAll('[data-ed-goto]').forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        self.jump(parseInt(el.getAttribute('data-ed-goto'), 10) || 0);
      });
    });

    this.root.querySelectorAll('[data-ed-accordion-trigger]').forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var panel = trigger.parentElement.querySelector('[data-ed-accordion-panel]');
        var open = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', open ? 'false' : 'true');
        if (panel) panel.hidden = open;
        var glyph = trigger.querySelector('[data-ed-accordion-glyph]');
        if (glyph) glyph.textContent = open ? '+' : '−';
      });
    });
  };

  EditorialScroll.prototype.scrollable = function () {
    return !!this.scroller && this.scroller.scrollHeight - this.scroller.clientHeight > 4;
  };

  EditorialScroll.prototype.onScroll = function () {
    if (!this.scroller) return;
    if (!this.lock) {
      var n = Math.round(this.scroller.scrollTop / this.scroller.clientHeight);
      if (n !== this.active) { this.active = n; this.render(); }
    }
    clearTimeout(this.settleTimer);
    var self = this;
    this.settleTimer = setTimeout(function () { self.settle(); }, 160);
  };

  EditorialScroll.prototype.settle = function () {
    if (this.lock || !this.scroller) return;
    var h = this.scroller.clientHeight;
    var target = Math.round(this.scroller.scrollTop / h) * h;
    if (Math.abs(this.scroller.scrollTop - target) > 2) this.jump(Math.round(target / h));
  };

  EditorialScroll.prototype.jump = function (n) {
    if (!this.scroller) return;
    var i = Math.max(0, Math.min(this.sections.length - 1, n));
    var target = i * this.scroller.clientHeight;
    this.lock = true;
    this.active = i;
    this.render();

    var self = this;
    clearTimeout(this.unlockTimer);
    var finish = function () {
      self.scroller.removeEventListener('scrollend', finish);
      clearTimeout(self.unlockTimer);
      if (Math.abs(self.scroller.scrollTop - target) > 4) self.scroller.scrollTop = target;
      self.lock = false;
    };
    // `scrollend` fires exactly when the smooth-scroll animation is actually
    // done — far more reliable than guessing a fixed duration. The timeout is
    // just a safety net in case scrollend never fires (old browser, or the
    // animation gets interrupted).
    this.scroller.addEventListener('scrollend', finish, { once: true });
    this.unlockTimer = setTimeout(finish, 1000);
    this.scroller.scrollTo({ top: target, behavior: 'smooth' });
  };

  EditorialScroll.prototype.onWheel = function (e) {
    // A lightbox or other modal is open — leave scrolling/wheel to it instead
    // of jumping the background page between sections.
    if (document.documentElement.hasAttribute('data-ed-modal-open')) return;
    if (Math.abs(e.deltaY) < 3 || !this.scrollable()) return;
    var inner = e.target && e.target.closest ? e.target.closest('[data-inner-scroll]') : null;
    if (inner && inner.scrollHeight - inner.clientHeight > 4) {
      var room = e.deltaY > 0
        ? inner.scrollHeight - inner.clientHeight - inner.scrollTop > 1
        : inner.scrollTop > 1;
      if (room) return;
    }
    e.preventDefault();

    // A single flick/scroll fires a long burst of wheel events (trackpad
    // inertia can tail off for over a second). Treat consecutive events with
    // no meaningful gap as ONE gesture — only its first tick may trigger a
    // jump — instead of only guarding on the jump-animation lock, which a
    // long burst can easily outlast and cause a second, unwanted jump.
    var now = Date.now();
    var isNewGesture = now - this.lastWheelAt > 220;
    this.lastWheelAt = now;
    if (!isNewGesture || this.lock) return;

    this.jump(this.active + (e.deltaY > 0 ? 1 : -1));
  };

  EditorialScroll.prototype.onKey = function (e) {
    // A lightbox or other modal is open — its own keydown handling (arrows,
    // Escape) takes over instead of jumping the background page.
    if (document.documentElement.hasAttribute('data-ed-modal-open')) return;
    var keys = { ArrowDown: 1, PageDown: 1, ' ': 1, ArrowUp: -1, PageUp: -1 };
    if (!this.scrollable()) return;
    if (e.target && /input|textarea/i.test(e.target.tagName)) return;
    var inner = e.target && e.target.closest ? e.target.closest('[data-inner-scroll]') : null;
    if (inner && inner.scrollHeight - inner.clientHeight > 4) {
      var d0 = keys[e.key];
      var room = d0 > 0
        ? inner.scrollHeight - inner.clientHeight - inner.scrollTop > 1
        : inner.scrollTop > 1;
      if (d0 && room) return;
    }
    // Key-repeat from a held-down arrow key fires many keydown events per
    // second — without this guard, each one restarted the scroll animation
    // from wherever it currently was, which is what caused the fast/glitchy
    // motion. Only one jump is allowed in flight at a time.
    if (this.lock) {
      if (keys[e.key] || e.key === 'Home' || e.key === 'End') e.preventDefault();
      return;
    }
    if (e.key === 'Home') { e.preventDefault(); this.jump(0); return; }
    if (e.key === 'End') { e.preventDefault(); this.jump(this.sections.length - 1); return; }
    var d = keys[e.key];
    if (!d) return;
    e.preventDefault();
    this.jump(this.active + d);
  };

  EditorialScroll.prototype.render = function () {
    var label = (this.sections[this.active] && this.sections[this.active].getAttribute('data-screen-label')) || '';
    if (this.numEl) this.numEl.textContent = String(this.active + 1).padStart(2, '0') + ' / ' + String(this.sections.length).padStart(2, '0');
    if (this.labelEl) this.labelEl.textContent = label;
    this.dots.forEach(function (dot, i) { dot.classList.toggle('is-active', i === this.active); }, this);
    this.root.querySelectorAll('[data-ed-cue]').forEach(function (cue) {
      cue.hidden = this.active !== 0;
    }, this);
  };

  document.addEventListener('DOMContentLoaded', function () {
    initTickerHeightSync();
    document.querySelectorAll('[data-editorial-root]').forEach(function (root) {
      new EditorialScroll(root);
    });
  });
})();
